import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { PdfExtractionPanel } from "@/components/containers/PdfExtractionPanel";
import { UnresolvedSkuPanel, boxTotals } from "@/components/containers/UnresolvedSkuPanel";
import { DraftPicker, LeaveConfirm } from "@/components/containers/ImportDraftControls";
import { FinalImportSummary } from "@/components/containers/FinalImportSummary";

const resPayload = (skuRes) => Object.fromEntries(Object.entries(skuRes).map(([row, v]) => [row, v.exclude ? { exclude: true } : { sku: v.sku }]));

export const PackingListImport = ({ onCommitted }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [resolutions, setResolutions] = useState({});
  const [skuRes, setSkuRes] = useState({});
  const [ack, setAck] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [leave, setLeave] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [final, setFinal] = useState(null); // ID-077: { key, data | error } — server-computed confirm payload
  const finalKey = preview ? `${preview.preview_id}|${preview.catalog_rechecked_at || ""}|${JSON.stringify(resPayload(skuRes))}` : "";
  useEffect(() => {
    if (!open || !preview?.preview_id) { setFinal(null); return undefined; }
    const key = finalKey;
    const t = setTimeout(() => {
      api.post(`/containers/import-v51/${preview.preview_id}/final`, { sku_resolutions: resPayload(skuRes) })
        .then(({ data }) => setFinal({ key, data }))
        .catch((e) => setFinal({ key, error: formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) }));
    }, 250);
    return () => clearTimeout(t);
  }, [open, finalKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) api.get("/admin/suppliers").then(({ data }) => setSuppliers(data.filter((s) => s.active))).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!open || preview) { return; }
    api.get("/containers/import-v51/drafts").then(({ data }) => { setDrafts(data); setShowDrafts(data.length > 0); }).catch(() => {});
  }, [open, preview]);

  // ID-076: autosave the review state onto the existing preview record (short debounce)
  const draftBody = () => ({ sku_resolutions: skuRes, supplier_resolutions: resolutions, ack, file_name: fileName });
  const saveDraft = async () => {
    if (!preview?.preview_id) return;
    setSaving(true);
    try { const { data } = await api.put(`/containers/import-v51/${preview.preview_id}/draft`, draftBody()); setSavedAt(data.saved_at); }
    catch { /* preview committed or gone — nothing to save */ } finally { setSaving(false); }
  };
  useEffect(() => {
    if (!open || !preview?.preview_id) { return undefined; }
    const t = setTimeout(saveDraft, 700);
    return () => clearTimeout(t);
  }, [open, preview, skuRes, resolutions, ack]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !preview || !saving) { return undefined; }
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, preview, saving]);
  const reset = () => { setPreview(null); setFileName(""); setSkuRes({}); setResolutions({}); setAck(false); setSavedAt(null); setLeave(false); };
  const closeAll = () => { reset(); setOpen(false); };
  const resume = async (pid) => {
    try {
      const { data } = await api.get(`/containers/import-v51/${pid}`);
      const d = data.draft || {};
      const res = d.sku_resolutions || {};
      const skus = Object.values(res).filter((v) => v?.sku).map((v) => v.sku);
      const { data: rv } = await api.post(`/containers/import-v51/${pid}/revalidate`, { skus }); // ID-074 on resume
      setPreview(rv.changed ? rv.preview : data);
      setSkuRes(Object.fromEntries(Object.entries(res).filter(([, v]) => !(v?.sku && rv.missing_skus.includes(v.sku)))));
      setResolutions(d.supplier_resolutions || {});
      setAck(rv.changed || rv.missing_skus.length ? false : !!d.ack);
      setFileName(d.file_name || data.filename);
      setSavedAt(d.updated_at);
      setShowDrafts(false);
      if (rv.changed || rv.missing_skus.length) toast.info(`Catalog changed since the draft — lines rechecked${rv.missing_skus.length ? ` (${rv.missing_skus.join(", ")} no longer ${rv.missing_skus.length === 1 ? "exists" : "exist"})` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ID-074: re-check the saved preview against the current catalog (product deleted/created elsewhere)
  const checking = useRef(false);
  const recheck = async (silent = false) => {
    if (!preview?.preview_id || checking.current) return false;
    checking.current = true;
    const skus = Object.values(skuRes).filter((v) => v.sku).map((v) => v.sku);
    try {
      const { data } = await api.post(`/containers/import-v51/${preview.preview_id}/revalidate`, { skus });
      if (data.changed) setPreview(data.preview);
      if (data.missing_skus.length) setSkuRes((r) => Object.fromEntries(Object.entries(r).filter(([, v]) => !data.missing_skus.includes(v.sku))));
      if ((data.changed || data.missing_skus.length) && !silent) toast.info(`Catalog changed — lines rechecked${data.missing_skus.length ? ` (${data.missing_skus.join(", ")} no longer ${data.missing_skus.length === 1 ? "exists" : "exist"})` : ""}`);
      if (data.changed || data.missing_skus.length) setAck(false);
      return data.changed || data.missing_skus.length > 0;
    } catch { return false; } finally { checking.current = false; }
  };
  useEffect(() => {
    if (!open || !preview?.preview_id) { return undefined; }
    const onFocus = () => { if (document.visibilityState === "visible") recheck(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setPreview(null);
    setSkuRes({});
    setAck(false);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/containers/import-v51/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/containers/import-v51/commit", { preview_id: preview.preview_id, supplier_resolutions: resolutions, sku_resolutions: resPayload(skuRes),
        acknowledged_excluded_boxes: ack ? fin.excluded_boxes : null, expected_total_boxes: fin.total_boxes });
      toast.success(`Imported: ${data.created.length} new, ${data.amended.length} amended, ${data.unchanged.length} unchanged${data.skipped_locked.length ? `, ${data.skipped_locked.length} locked` : ""}`);
      setOpen(false);
      reset();
      onCommitted?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "CATALOG_CHANGED") { await recheck(true); toast.error(d.message); }
      else if (d?.code === "IMPORT_PAYLOAD_INCONSISTENT") toast.error(d.message);
      else toast.error(formatApiErrorDetail(d));
    } finally {
      setBusy(false);
    }
  };

  const bt = preview ? boxTotals(preview, skuRes) : null;
  const fin = final?.key === finalKey ? final.data : null;
  const finErr = final?.key === finalKey ? final.error : null;
  const committable = preview && fin && preview.containers.some((c) => !c.locked) && !preview.already_imported
    && (preview.unresolved_suppliers || []).every((u) => resolutions[u.raw])
    && bt.unresolved === 0 && fin.unresolved_boxes === 0 && fin.excluded_boxes === bt.excluded && (bt.excluded === 0 || ack)
    && fin.resolved_boxes === bt.resolved && fin.total_boxes > 0;

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (o) { setOpen(true); } else if (preview) { setLeave(true); } else { closeAll(); } }}>
      <DialogTrigger asChild>
        <Button data-testid="import-v51-button" className="h-11 w-full bg-charcoal text-white hover:bg-charcoal/90 sm:w-auto">
          <FileSpreadsheet className="mr-2 h-4 w-4" /> Import packing list (Excel)
        </Button>
      </DialogTrigger>
      <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}
        className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-5xl overflow-y-auto border-border bg-white sm:w-full" data-testid="import-v51-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Import container packing list</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            Seven columns: Product Family · Supplier · Container · Container Shipping Number · ETA Warehouse · Product SKU · Total Boxes (six-column files without Supplier remain valid). <a href="/downloads/Zelligio_Packing_List_Template_v51.xlsx" className="underline" data-testid="import-v51-template">Download template</a>. Products resolve by exact Catalog SKU; names and specifications always come from the Catalog. Imported goods start in Morocco.
          </DialogDescription>
        </DialogHeader>
        {showDrafts && !preview && <DraftPicker drafts={drafts} onResume={resume} onStartNew={() => setShowDrafts(false)} />}
        {preview && (
          <p className="text-xs text-warmgrey" data-testid="import-draft-status">{saving ? "Saving draft…" : savedAt ? `Draft saved · ${new Date(savedAt).toLocaleTimeString()}` : ""}</p>
        )}
        {!(showDrafts && !preview) && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Packing list file (.xlsx / .xls / .csv / .pdf)</p>
          <input
            type="file"
            accept=".xlsx,.xls,.csv,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/pdf"
            onChange={handleFile}
            data-testid="import-v51-file"
            className="block w-full cursor-pointer rounded-md border border-border bg-alabaster p-3 text-sm text-charcoal file:mr-3 file:rounded-md file:border-0 file:bg-terracotta file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
          />
          {fileName && <p className="text-xs text-warmgrey" data-testid="import-v51-filename">Selected: {fileName}</p>}
        </div>
        )}
        {busy && !preview && <Loader2 className="mt-4 h-5 w-5 animate-spin text-terracotta" />}
        {preview && (
          <div className="mt-5 space-y-5" data-testid="import-v51-preview">
            {preview.already_imported && (
              <p className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-sm text-charcoal" data-testid="import-v51-already">
                This exact file was already committed on {new Date(preview.prior_import_at).toLocaleString()} — re-importing is blocked so no quantities are duplicated.
              </p>
            )}
            {preview.pdf_extraction && <PdfExtractionPanel pdf={preview.pdf_extraction} finalPallets={fin?.pallets} />}
            <UnresolvedSkuPanel preview={preview} skuRes={skuRes} setSkuRes={setSkuRes} ack={ack} setAck={setAck} />
            {fin ? <FinalImportSummary preview={preview} final={fin} /> : finErr ? (
              <p className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-sm text-charcoal" data-testid="import-v51-final-error">Import cannot be confirmed: {finErr}</p>
            ) : <Loader2 className="h-5 w-5 animate-spin text-terracotta" data-testid="import-v51-final-loading" />}
            {preview.format && <p className="text-xs text-warmgrey" data-testid="import-v51-format">Detected format: {preview.format}</p>}
            {preview.unresolved_suppliers?.length > 0 && (
              <div className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-xs text-charcoal" data-testid="import-v51-unresolved-suppliers">
                <p className="font-semibold">Suppliers not in the Supplier Directory — resolve before committing:</p>
                {preview.unresolved_suppliers.map((u) => (
                  <div key={u.raw} className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="font-mono">"{u.raw}"</span> (rows {u.rows.join(", ")}) →
                    <select data-testid={`supplier-resolve-${u.raw}`} value={resolutions[u.raw] || ""} onChange={(e) => setResolutions((r) => ({ ...r, [u.raw]: e.target.value }))} className="h-8 rounded-md border border-border bg-white px-2">
                      <option value="">Choose…</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>Match: {s.name} ({s.code})</option>)}
                      <option value={`CREATE:${u.normalized.split(" ").map((w) => w[0]).join("").slice(0, 4)}`}>Create new supplier "{u.raw}"</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={commit} disabled={!committable || busy} data-testid="import-v51-commit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm import — goods remain in Morocco"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <LeaveConfirm open={leave} busy={saving} onContinue={() => setLeave(false)}
      onSaveExit={async () => { await saveDraft(); closeAll(); toast.success("Draft saved — resume it from Import packing list"); }}
      onDiscard={async () => { if (preview?.preview_id) await api.delete(`/containers/import-v51/${preview.preview_id}/draft`).catch(() => {}); closeAll(); toast.info("Draft discarded"); }} />
    </>
  );
};
